/**
 * TeamChatList — Coach's landing page for team chat management.
 * Lists all chats owned by the coach + chats they're a member of, and
 * lets them create a new chat scoped to one of their seasons.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquarePlus, Users, ChevronRight, Loader2, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Layout } from '../components/Layout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamChatList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [coachChats, setCoachChats] = useState([]);
  const [memberChats, setMemberChats] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newSeasonId, setNewSeasonId] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [chatsRes, seasonsRes] = await Promise.all([
        axios.get(`${API}/team-chat`, { headers }),
        axios.get(`${API}/seasons`, { headers }).catch(() => ({ data: [] })),
      ]);
      setCoachChats(chatsRes.data.coach_chats || []);
      setMemberChats(chatsRes.data.member_chats || []);
      const s = seasonsRes.data || [];
      setSeasons(s);
      if (s.length && !newSeasonId) setNewSeasonId(s[0].id);
    } catch (err) {
      toast.error('Failed to load chats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createChat = async () => {
    if (!newTitle.trim() || !newSeasonId) return;
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/team-chat`,
        { title: newTitle.trim(), season_id: newSeasonId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Chat created');
      setShowCreate(false);
      setNewTitle('');
      navigate(`/team-chat/${res.data.id}/manage`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create chat');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Layout>
    <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="team-chat-list-page">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-black text-gray-900">Team Chat</h1>
          <p className="text-sm text-gray-500 mt-1">
            Invite-only chats with parents &amp; players. One or more per season.
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="bg-[#007a33] hover:bg-[#005c26] text-white shrink-0"
          data-testid="create-chat-btn"
        >
          <MessageSquarePlus className="w-4 h-4 mr-1.5" />
          New chat
        </Button>
      </div>

      <div className="mb-6 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-900 flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          M1 available: create chats, invite parents/players, control per-member feature access.
          Announcements, event RSVPs, and DMs arrive in the next release.
        </span>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="w-8 h-8 mx-auto text-gray-400 animate-spin" /></div>
      ) : (
        <>
          <section className="mb-8" data-testid="coach-chats-section">
            <h2 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-2">Chats you manage</h2>
            {coachChats.length === 0 ? (
              <div className="p-6 rounded-lg border border-dashed border-gray-300 text-center text-sm text-gray-500">
                No chats yet. Create one to get started.
              </div>
            ) : (
              <ul className="space-y-2">
                {coachChats.map((c) => (
                  <li key={c.id}>
                    <Link
                      to={`/team-chat/${c.id}/manage`}
                      className="flex items-center justify-between p-3 rounded-lg bg-white border border-gray-200 hover:border-[#007a33] hover:shadow-sm transition"
                      data-testid={`coach-chat-${c.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 truncate">{c.title}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {c.season_name} · {c.member_count || 0} member{c.member_count === 1 ? '' : 's'}
                          {c.status === 'archived' && <span className="text-amber-600"> · archived</span>}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {memberChats.length > 0 && (
            <section data-testid="member-chats-section">
              <h2 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-2">You&apos;re a member of</h2>
              <ul className="space-y-2">
                {memberChats.map((c) => (
                  <li key={c.id}>
                    <Link
                      to={`/team-chat/${c.id}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-white border border-gray-200 hover:border-[#007a33] hover:shadow-sm transition"
                      data-testid={`member-chat-${c.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 truncate">{c.title}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {c.coach_name}{c.coach_team ? ` · ${c.coach_team}` : ''} · {c.season_name}
                        </p>
                      </div>
                      <Users className="w-4 h-4 text-gray-400 shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-chat-dialog">
          <DialogHeader>
            <DialogTitle>Create a team chat</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Chat title</Label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g. Under-14s First Team"
                maxLength={80}
                data-testid="new-chat-title"
              />
            </div>
            <div>
              <Label>Season</Label>
              <select
                value={newSeasonId}
                onChange={(e) => setNewSeasonId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
                data-testid="new-chat-season"
              >
                {seasons.length === 0 && <option value="">No seasons — create one first</option>}
                {seasons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              onClick={createChat}
              disabled={!newTitle.trim() || !newSeasonId || creating}
              className="bg-[#007a33] hover:bg-[#005c26] text-white"
              data-testid="create-chat-confirm-btn"
            >
              {creating ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Creating…</> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
